import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LoadingScreen from './components/LoadingScreen';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionGate from './components/PermissionGate';
import Layout from './components/Layout';

// Auth
import Login from './pages/auth/Login';
const Register = lazy(() => import('./pages/auth/Register'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const EmailVerified = lazy(() => import('./pages/auth/EmailVerified'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));

// Customer
const CustomerDashboard = lazy(() => import('./pages/customer/CustomerDashboard'));
const CustomerProfile = lazy(() => import('./pages/customer/Profile'));
const CustomerProducts = lazy(() => import('./pages/customer/Products'));
const CustomerCart = lazy(() => import('./pages/customer/Cart'));
const CustomerOrders = lazy(() => import('./pages/customer/CustomerOrders'));
const CustomerNotifications = lazy(() => import('./pages/customer/CustomerNotifications'));
const CustomerWallet = lazy(() => import('./pages/customer/Wallet'));
const UploadOrder = lazy(() => import('./pages/customer/UploadOrder'));
const Stores = lazy(() => import('./pages/customer/Stores'));
const StorePage = lazy(() => import('./pages/customer/StorePage'));
const StoreCart = lazy(() => import('./pages/customer/StoreCart'));
const StoreUploadOrder = lazy(() => import('./pages/customer/StoreUploadOrder'));

// Vendor
const VendorDashboard = lazy(() => import('./pages/vendor/Dashboard'));
const VendorProfile = lazy(() => import('./pages/vendor/Profile'));
const VendorProducts = lazy(() => import('./pages/vendor/Products'));
const VendorQuotes = lazy(() => import('./pages/vendor/VendorQuotes'));
const VendorOrders = lazy(() => import('./pages/vendor/VendorOrders'));
const VendorAccount = lazy(() => import('./pages/vendor/Account'));
const VendorReports = lazy(() => import('./pages/vendor/Reports'));

// Admin
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminVendors = lazy(() => import('./pages/admin/Vendors'));
const AdminProducts = lazy(() => import('./pages/admin/Products'));
const AdminQuotes = lazy(() => import('./pages/admin/AdminQuotes'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminPayments = lazy(() => import('./pages/admin/Payments'));
const AdminStaff = lazy(() => import('./pages/admin/Staff'));
const AdminReports = lazy(() => import('./pages/admin/Reports'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const ImageOrders = lazy(() => import('./pages/admin/ImageOrders'));
const AdminCustomerWallets = lazy(() => import('./pages/admin/CustomerWallets'));
const AdminVendorsPayouts = lazy(() => import('./pages/admin/VendorsPayouts'));
const AdminRankManagement = lazy(() => import('./pages/admin/RankManagement'));
const AdminApprovalRequests = lazy(() => import('./pages/admin/ApprovalRequests'));
const AdminLedgerDashboard = lazy(() => import('./pages/admin/LedgerDashboard'));
const AdminTreasury = lazy(() => import('./pages/admin/Treasury'));
const AdminPromotions = lazy(() => import('./pages/admin/AdminPromotions'));
const AdminAgents = lazy(() => import('./pages/admin/Agents'));
const AdminSponsors = lazy(() => import('./pages/admin/Sponsors'));
const ServerHealth = lazy(() => import('./pages/admin/ServerHealth')); // <-- NEW IMPORT
const AgentPortal = lazy(() => import('./pages/agent/AgentPortal'));
const AgentNotifications = lazy(() => import('./pages/agent/AgentNotifications'));

// Unknown URLs go to the signed-in user's own home — never to /login while a
// session exists (a missing route must not masquerade as a logout).
const CatchAllRedirect = () => {
  const { user, isAdmin, isVendor, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (isVendor) return <Navigate to="/vendor" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        {/* Light-theme toast. Colours are hardcoded on purpose: App.js is shared
            by the customer and admin trees, and importing either theme file here
            would couple the router to one side of the app. Values match
            src/styles/theme.js (C.surface / C.ink / C.border) by hand. */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#FFFFFF',
              color: '#0F172A',
              border: '1px solid #E2E8F0',
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              fontSize: '14px',
            },
            success: { iconTheme: { primary: '#16A34A', secondary: '#FFFFFF' } },
            error: { iconTheme: { primary: '#EF4444', secondary: '#FFFFFF' } },
          }}
        />

        <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/email-verified" element={<EmailVerified />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerProfile />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerProducts />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerCart />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute role="customer">
                <Navigate to="/orders" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerOrders />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerNotifications />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload-order"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <UploadOrder />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <CustomerWallet />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/stores"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <Stores />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stores/:storeId"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <StorePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stores/:storeId/cart"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <StoreCart />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stores/:storeId/upload"
            element={
              <ProtectedRoute role="customer">
                <Layout>
                  <StoreUploadOrder />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/vendor"
            element={
              <ProtectedRoute role="vendor">
                <Layout>
                  <VendorDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/profile"
            element={
              <ProtectedRoute role="vendor">
                <Layout>
                  <VendorProfile />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/products"
            element={
              <ProtectedRoute role="vendor" requireVerifiedVendor={true}>
                <Layout>
                  <VendorProducts />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/quotes"
            element={
              <ProtectedRoute role="vendor" requireVerifiedVendor={true}>
                <Layout>
                  <VendorQuotes />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/orders"
            element={
              <ProtectedRoute role="vendor" requireVerifiedVendor={true}>
                <Layout>
                  <VendorOrders />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/account"
            element={
              <ProtectedRoute role="vendor" requireVerifiedVendor={true}>
                <Layout>
                  <VendorAccount />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/reports"
            element={
              <ProtectedRoute role="vendor" requireVerifiedVendor={true}>
                <Layout>
                  <VendorReports />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <AdminNotifications />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ledger"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_ledger">
                    <AdminLedgerDashboard />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/promotions"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_treasury">
                    <AdminPromotions />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agents"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_treasury">
                    <AdminAgents />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sponsors"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_treasury">
                    <AdminSponsors />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Agents log in with a normal account; the portal itself checks
              whether that account is registered as an agent. */}
          <Route
            path="/agent/notifications"
            element={
              <ProtectedRoute role="agent">
                <Layout>
                  <AgentNotifications />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent"
            element={
              <ProtectedRoute role="agent">
                <Layout>
                  <AgentPortal />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/treasury"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_treasury">
                    <AdminTreasury />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quotes"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_process_quotes">
                    <AdminQuotes />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/image-orders"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_process_quotes">
                    <ImageOrders />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_update_delivery">
                    <AdminOrders />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_verify_payments">
                    <AdminPayments />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customer-wallets"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_customer_wallets">
                    <AdminCustomerWallets />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vendor-payouts"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_vendor_payouts">
                    <AdminVendorsPayouts />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/approval-requests"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_approvals">
                    <AdminApprovalRequests />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_manage_users">
                    <AdminUsers />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vendors"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_manage_users">
                    <AdminVendors />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_manage_products">
                    <AdminProducts />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_reports">
                    <AdminReports />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="super_admin">
                    <AdminStaff />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ranks"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="can_view_ranks">
                    <AdminRankManagement />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="super_admin">
                    <AdminSettings />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/server-health"
            element={
              <ProtectedRoute role="admin">
                <Layout>
                  <PermissionGate permission="super_admin">
                    <ServerHealth />
                  </PermissionGate>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;